import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Trash2, Loader2, ShoppingCart } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useOwned } from "@/lib/useOwned";
import { getRef } from "@/lib/affiliate";

const PROVIDERS = [
  { id: "pesapal", label: "PesaPal" },
  { id: "nowpayments", label: "Crypto" },
];

export default function CartDrawer() {
  const { items, subtotal, open, setOpen, remove, clear } = useCart();
  const { user } = useAuth();
  const owned = useOwned();
  const navigate = useNavigate();
  const [provider, setProvider] = useState("pesapal");
  const [busy, setBusy] = useState(false);

  const checkout = async () => {
    if (!user) {
      toast.info("Sign in to complete your purchase");
      setOpen(false);
      navigate("/login");
      return;
    }
    if (items.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.id, quantity: 1, plan: i.plan || undefined })),
        provider,
        ref_code: getRef() || undefined,
      });
      clear();
      setOpen(false);
      if (data.checkout_url && /^https?:\/\//.test(data.checkout_url)) window.location.href = data.checkout_url;
      else navigate(`/checkout/pay/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start checkout");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60]" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
      <aside className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col border-l border-emerald-500/25 bg-[#0b2115] shadow-2xl">
        <div className="flex items-center justify-between border-b border-emerald-500/15 p-5">
          <div className="flex items-center gap-2 text-white">
            <ShoppingCart size={18} className="text-lime-300" />
            <p className="font-editorial text-lg">Your cart ({items.length})</p>
          </div>
          <button onClick={() => setOpen(false)} data-testid="cart-close" className="rounded-full p-1.5 text-emerald-100/60 hover:bg-white/5 hover:text-white"><X size={18} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {items.length === 0 ? (
            <p className="mt-10 text-center text-sm text-emerald-100/60">Your cart is empty.</p>
          ) : (
            <div className="grid gap-3">
              {items.map((i) => (
                <div key={i.id} className="flex items-center gap-3 rounded-lg border border-emerald-500/12 bg-[#0d2416]/60 p-3" data-testid={`cart-item-${i.id}`}>
                  <img src={i.image_url || "/logo-mark.png"} alt="" className="h-12 w-12 rounded object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-white">{i.title}</p>
                    <p className="text-xs text-lime-300">${Number(i.price).toFixed(2)}</p>
                    {owned.has(i.id) && (
                      <span className="mt-1 inline-block chip chip-emerald" data-testid={`cart-owned-${i.id}`}>Already owned</span>
                    )}
                  </div>
                  <button onClick={() => remove(i.id)} data-testid={`cart-remove-${i.id}`} className="text-emerald-100/50 hover:text-red-300"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-emerald-500/15 p-5">
            <div className="mb-3 flex items-center justify-between text-sm">
              <span className="text-emerald-100/70">Subtotal</span>
              <span className="font-editorial text-xl text-white" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
            </div>
            <div className="mb-3 grid grid-cols-2 gap-2">
              {PROVIDERS.map((p) => (
                <button key={p.id} onClick={() => setProvider(p.id)} data-testid={`cart-provider-${p.id}`}
                  className={`rounded-lg border px-3 py-2 text-xs ${provider === p.id ? "border-lime-400/60 bg-emerald-500/10 text-white" : "border-emerald-500/15 text-emerald-100/60"}`}>
                  {p.label}
                </button>
              ))}
            </div>
            <button onClick={checkout} disabled={busy} data-testid="cart-checkout" className="btn-edge w-full justify-center">
              {busy ? <Loader2 size={16} className="animate-spin" /> : `Checkout · $${subtotal.toFixed(2)}`}
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}
